import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  DestroyRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MdbFormsModule } from 'mdb-angular-ui-kit/forms';
import { TranslateModule } from '@ngx-translate/core';

const SEARCH_CONTROL_NAME = 'search';

@Component({
  selector: 'app-text-search',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ReactiveFormsModule, MdbFormsModule, TranslateModule],
  templateUrl: './text-search.component.html',
  styleUrl: './text-search.component.scss',
})
export class TextSearchComponent {
  /** Current value (from parent); synced into internal control. */
  value = input<string>('');
  /** i18n key for placeholder. */
  placeholderKey = input<string>('SEARCH');
  /** i18n key for the floating label (same as in dropdowns). */
  labelKey = input<string | undefined>();
  /** When true, show a clear (×) button when the input has value. */
  showClearButton = input(false);
  /** Optional id for the input (a11y). */
  inputId = input<string | undefined>();
  /** Emitted when the user changes the value (trimmed string or null when cleared). */
  valueChange = output<string | null>();
  /** Disabled state (e.g. while list is loading). */
  disabled = input(false);

  readonly form = new FormGroup({
    [SEARCH_CONTROL_NAME]: new FormControl<string>('', { nonNullable: false }),
  });

  readonly searchControl = this.form.get(SEARCH_CONTROL_NAME) as FormControl<string | null>;
  readonly searchControlName = SEARCH_CONTROL_NAME;
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const v = this.value();
      this.searchControl.setValue(v ?? '', { emitEvent: false });
    });

    effect(() => {
      const d = this.disabled();
      if (d) {
        this.searchControl.disable({ emitEvent: false });
      } else {
        this.searchControl.enable({ emitEvent: false });
      }
    });

    this.searchControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((v) => {
      const trimmed = typeof v === 'string' ? (v.trim() || null) : null;
      this.valueChange.emit(trimmed);
    });
  }

  clear(): void {
    this.searchControl.setValue('', { emitEvent: true });
  }
}
